import {
	type Panel,
	usePreviewPanelsActions,
	usePreviewPanelsContext,
} from '@atlassian/preview-panels-api';

import { useCloseAllOpenSidePanels } from './useCloseAllOpenSidePanels';

interface SwapPanelParams {
	panel: Panel;
	width?: number;
	onOpen?: () => void;
	onClose?: () => void;
}

/*
TODOS:
0. create a usePreviewPanelState hook to return active panel, if panel is open, width, etc.
1. return swap function
2. update openpreview panel function to:
- params should reflect the "open" params from the original open preview panel function
- do initial check to see if panel is already open and if types match: if so call swap otherwise continue
- check ari or panel type to determine which open function to call (this can be a switch statement maybe)
3. May need an abstracted open function just for the is open & swap logic
4. Update isPreviewPanelAvailable to leverage any-ari to ensure ari value is standardized
5. use 4 above to implement 2
6. update readme to reflect new way people need to "register" their panel open & close functions
7. update written docs in confluence to do 6 above
8. update where panel state is being pulled directly to use 0. above

*/
export const useClosePreviewPanel = () => {
	const context = usePreviewPanelsContext();
	const { close, swap } = usePreviewPanelsActions();
	const { closeAllOpenSidePanels } = useCloseAllOpenSidePanels();

	const swapPreviewPanels = ({ panel, width, onOpen, onClose }: SwapPanelParams): void => {
		swap({
			panel,
			width,
			onOpen,
			onClose,
		});
	};

	/*
	NOTE PLEASE READ:
	closeOnlyPreviewPanel() will only be used for closing any open preview panel when
	the confluence content wrapper panels (aka object sidebar) are opened. Outside of this scenario
	this function SHOULD NOT BE USED for closing preview panels. Once content wrapper
	panels are migrated to preview panels, this function will be deprecated.
	*/
	const closeOnlyPreviewPanel = (onClose?: () => void): void => {
		if (context?.state?.panel) {
			close(onClose);
		}
	};

	const closeAllPanels = (onClose?: () => void): void => {
		closeOnlyPreviewPanel(onClose);
		closeAllOpenSidePanels();
	};

	return { closeAllPanels, closeOnlyPreviewPanel, swapPreviewPanels };
};
